import debounce from 'lodash/debounce';
import PreOrder from './components/PreOrder';
import style from './index.module.scss';
import Product from './components/Product';
import Bundles from './components/Bundles';
import FAQ from './components/FAQ';
import Specs from './components/Specs';
import Media from './components/Media';
import Download from './components/Download';
import Loading from '@/components/UI/Loading';
import { GetUrlParams } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import EFCalculatorIcon from '@/components/UI/svg/Icons/EFCalculatorIcon';
import { Provider } from '@/components/PowerKitPreSale/context';

//滚动到tab处
function scrollToAnchor() {
    const ele = document.querySelector('.pre-sale-header') as HTMLElement;
    if (ele && typeof window !== 'undefined' && window.scrollTo) {
        const offsetHeight = (ele.offsetHeight || 0) + 150;
        window.scrollTo(0, offsetHeight);
    }
}

const CalculatorIcon = () => {
    const { useState, useEffect, useRouter } = useGlobal();
    const { locale } = useRouter();
    const [isCalIconShow, setIsCalIconShow] = useState(false);
    let interObserver: IntersectionObserver | null;

    useEffect(() => {
        const apperCb = (params: any) => {
            const data = params[0];
            const calIconStatus = data.intersectionRatio <= 0 && data.boundingClientRect.y < 0;
            setIsCalIconShow(calIconStatus);
        };
        interObserver && interObserver.disconnect();
        interObserver = new IntersectionObserver(apperCb);
        const helpEl = document.querySelector('.help-choose') as HTMLElement;
        if (helpEl) {
            interObserver.observe(helpEl);
        }

        return () => {
            interObserver && interObserver.disconnect();
        };
    }, []);

    const redirectToCal = () => {
        let calculatorUrl = `/${locale}/calculators/power-kits`;
        if (!location.origin) {
            // ie的一些老版本不兼容
            calculatorUrl = `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}/${locale}/calculators/power-kits`;
        }
        // 增加name是为了防止点击打开多个calculator/mm100页面
        window.open(calculatorUrl, 'calculators_mm100');
    };

    return (
        <div style={{ display: !isCalIconShow || ['au', 'sa'].includes(locale) ? 'none' : '' }} onClick={redirectToCal} className="calculator-icon-wrapper">
            <EFCalculatorIcon />
        </div>
    );
};

export default function PowerKitPreSale({ activityId }: any) {
    const { useState, useEffect, useRouter, useLang, gtm } = useGlobal();

    const router = useRouter();
    const { locale } = router;
    const { t } = useLang('ecoflowpowerkitsseries');

    const [isShowLoading, setIsShowLoading] = useState(false);

    const setLoadingStatus = (value: boolean) => {
        setIsShowLoading(value);
    };

    const [activeTab, setActiveTab] = useState('product');

    // 加载的tab页（作为性能优化的一点，只有在用户点击对应的tab页，该tab页才会进行第一次渲染）
    const [loadedTabList, setLoadedTabList] = useState([]);

    useEffect(() => {
        const defactiveTab = GetUrlParams('activeTab') || 'product';
        setActiveTab(defactiveTab);
        setLoadedTabList([defactiveTab]);
    }, []);

    useEffect(() => {
        const popstate = function (e: PopStateEvent) {
            const state = e.state;
            let tempState;
            // 此时state中无activeTab记录，表示未经过本页面进行跳转
            // （activeTab这个state状态只有在该页面处理过），此时取router中的默认值
            if (!state.activeTab) {
                tempState = activeTab;
            } else {
                tempState = state.activeTab;
            }
            setActiveTab(tempState);
        };
        window.addEventListener('popstate', popstate);
        return function () {
            window.removeEventListener('popstate', popstate);
        };
    }, []);

    const pushState = (state: string) => {
        const curActivity = history.state?.activeTab ? history.state.activeTab : activeTab;
        // 当前tab未发生变化时阻止pushstate，防止产生重复state状态
        if (state === curActivity) return;
        const tempState = { activeTab: state };
        // 当tab页未在已加载列表中时，进行加载
        if (!loadedTabList.includes(state)) {
            setLoadedTabList([...loadedTabList, state]);
        }

        // 切换tab页
        setActiveTab(state);
        const urlParamsActiveTab = GetUrlParams('activeTab');
        let temp;
        // 先判断url中是否存在activeTab字段
        if (urlParamsActiveTab) {
            temp = location.href.replace(/activeTab=[^&]*/, `activeTab=${state}`);
        } else {
            temp = `${location.origin + location.pathname}?activeTab=${state}${location.search.slice(1) ? `&${location.search.slice(1)}` : ''}`;
        }
        history.pushState(tempState, '', temp);
        scrollToAnchor();
    };

    // 用于 PreOrder 组件的回调
    const preCallback = (typeStr: string) => {
        if (typeStr == '1') {
            pushState('bundles');
            scrollToAnchor();
        }
    };

    // gtm & fb 埋点
    const buriedSpots = (tab: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'pre_order_navigation',
            navigation_name: tab,
        });
        window?.fbq && window?.fbq('track', 'FindLocation');
    };

    const [scroll, setScroll] = useState('left');

    const onScroll = (e: any) => {
        const sliderEl = document.querySelector('.pre-sale-nav') as HTMLElement;
        if (e.target?.scrollLeft <= 0) {
            return setScroll('left');
        }
        if (e.target?.scrollWidth - e.target?.scrollLeft <= sliderEl?.offsetWidth) {
            return setScroll('right');
        }
        return setScroll('');
    };

    useEffect(() => {
        const renderFrostNav = debounce(() => {
            const lineEl = document.querySelector('.space-line') as HTMLElement;
            const proNavOffH = lineEl?.offsetTop + lineEl?.offsetHeight;
            const productNav: any = document.querySelector('.pre-sale-nav') as HTMLElement;
            productNav?.classList.toggle('frost', productNav?.offsetTop > proNavOffH);
        }, 100);

        document.addEventListener('scroll', renderFrostNav);
        return () => {
            document.removeEventListener('scroll', renderFrostNav);
        };
    }, []);

    return (
        <Provider>
            <div className={style.powerkitpresale}>
                <div className="pre-sale-header">
                    <PreOrder preCallback={preCallback} activityId={activityId} />
                </div>
                <div className={`space-line`}></div>
                <div className={`pre-sale-nav pre-sale-nav-${activeTab}`} onScroll={onScroll}>
                    <div className={`cover  ${scroll === 'left' ? 'left' : scroll === 'right' ? 'right' : ''}`}></div>
                    <div className="nav-wrapper">
                        <span
                            className={`nav-item ${activeTab === 'product' && 'active'}`}
                            onClick={() => {
                                pushState('product');
                                buriedSpots('product');
                            }}
                        >
                            {t('presale.producttab')}
                        </span>
                        {locale !== 'sa' && (
                            <span
                                className={`nav-item ${activeTab === 'bundles' && 'active'}`}
                                onClick={() => {
                                    pushState('bundles');
                                    buriedSpots('bundles');
                                }}
                            >
                                {t('presale.bundlestab')}
                            </span>
                        )}
                        <span
                            className={`nav-item ${activeTab === 'download' && 'active'}`}
                            onClick={() => {
                                pushState('download');
                                buriedSpots('download');
                            }}
                        >
                            {t('presale.downloadtab')}
                        </span>
                        <span
                            className={`nav-item ${activeTab === 'faq' && 'active'}`}
                            onClick={() => {
                                pushState('faq');
                                buriedSpots('faq');
                            }}
                        >
                            {t('presale.faqtab')}
                        </span>
                        <span
                            className={`nav-item ${activeTab === 'specs' && 'active'}`}
                            onClick={() => {
                                pushState('specs');
                                buriedSpots('specs');
                            }}
                        >
                            {t('presale.specstab')}
                        </span>
                        {!['kr', 'cn', 'it', 'es'].includes(locale) && (
                            <span
                                className={`nav-item ${activeTab === 'media' && 'active'}`}
                                onClick={() => {
                                    pushState('media');
                                    buriedSpots('media');
                                }}
                            >
                                {t('presale.mediatab')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="pre-sale-content">
                    {isShowLoading && <Loading />}
                    <div style={{ display: activeTab !== 'product' ? 'none' : 'block' }} className="product-wrapper">
                        <Product />
                    </div>
                    {loadedTabList.includes('bundles') && locale !== 'sa' && (
                        <div style={{ display: activeTab !== 'bundles' ? 'none' : 'block' }} className="bundles-wrapper">
                            <Bundles activityId={activityId} activeTab={activeTab} preCallback={preCallback} />
                        </div>
                    )}
                    {loadedTabList.includes('download') && (
                        <div style={{ display: activeTab !== 'download' ? 'none' : 'block' }} className="download-wrapper">
                            <Download />
                        </div>
                    )}
                    {loadedTabList.includes('faq') && (
                        <div style={{ display: activeTab !== 'faq' ? 'none' : 'block' }} className="faq-wrapper">
                            <FAQ />
                        </div>
                    )}
                    {loadedTabList.includes('specs') && (
                        <div style={{ display: activeTab !== 'specs' ? 'none' : 'block' }} className="media-wrapper">
                            <Specs />
                        </div>
                    )}
                    {!['kr', 'cn', 'it', 'es'].includes(locale) && (
                        <div style={{ display: activeTab !== 'media' ? 'none' : 'block' }} className="wrapper-media">
                            <Media />
                        </div>
                    )}
                </div>

                <CalculatorIcon />
                <style jsx global>
                    {`
                        .ant-back-top {
                            bottom: 250px !important;
                        }
                    `}
                </style>
            </div>
        </Provider>
    );
}
